import React, { useContext, useMemo } from 'react';
import { connect } from 'react-redux';
import { Button, List, ListItem } from '@mui/material';
import {
  ChangeCircle,
  Publish,
  PublishedWithChanges,
  Unpublished,
  ReportProblem,
  Warning,
} from '@mui/icons-material';
import StagingDataTable from './StagingDataTable';
import { Fill } from '../../util/Page';
import {
  demoteStaging,
  promoteEquipsTopicsAction,
  updateStagingEquipment,
  getPointDetailsAction,
  getEquipmentDetailsAction,
  stagingEquipmentDelete,
} from '../../../redux/actions/staging';
import ColumnDefinition from '../constants/ColumnDefinition';
import TableAction from '../constants/TableAction';
import { dateTime } from '../../../utils/parsing';
import { fetchBuildingDbo } from '../../../redux/actions/buildings';
import StatusBadge from '../constants/StatusBadge';
import PromotionDialog from './dialogs/PromotionDialog';
import { getEnv } from '../../../api/urls';
import FilterContext from '../contexts/FilterContext';
import { equipmentPromotionStatus, hasUnpublishedChanges } from '../util/equipment';
import { unique } from '../../../utils/collections';

const EquipmentDataTable = ({
  buildingId,
  onPromotionResults,
  selectedEquipIds,
  onItemSelected,

  equipment,
  equipTypes,
  promotedPointsById,
  promotedEquipmentById,
  deployments,

  promoteEquipment,
  updateStagingEquipmentUncaught,
  demoteStagingUncaught,
  deleteStagingEquipmentUncaught,
  fetchBuildingDboUncaught,
}) => {
  // Loading is about the page refreshing, not to be confused with loaded which is about waiting for staging data to generally become available
  const [loading, setLoading] = React.useState(false);
  const [promotionOpen, setPromotionOpen] = React.useState(false);
  const { filters } = useContext(FilterContext);

  // Never include SKIP equipment for equipment management since it's a completely ephemeral equipment that should not be managed or promoted
  const filteredEquipment = useMemo(
    () => Array.from(equipment.values().filter((equip) => filters.every((f) => f.filter(equip)))),
    [filters, equipment]
  );

  function extractDevice(equip, extractFn) {
    return unique((equip.devices ?? []).map((device) => extractFn(device)));
  }

  function buildValidationTooltip(equip) {
    if (equip.validation === undefined) return null;
    const pointValidations = Array.from(
      equip.points?.flatMap((point) =>
        (point.validation ?? []).filter(Boolean).map((v) => ({ point, ...v }))
      ) ?? []
    );

    return (
      <>
        {equip?.validation?.length > 0 && (
          <>
            This equipment contains validation errors:
            <List sx={{ listStyleType: 'disc' }}>
              {equip?.validation?.map((v) => (
                <ListItem key={v.field + v.error_class} sx={{ display: 'list-item', padding: 0 }}>
                  {v.field} - {v.message}
                </ListItem>
              ))}
            </List>
          </>
        )}

        {pointValidations.length > 0 && (
          <>
            This equipment contains point validation errors:
            <List sx={{ listStyleType: 'disc' }}>
              {pointValidations.slice(0, 5).map((v) => (
                <ListItem
                  key={v.point.topic + v.field + v.error_class}
                  sx={{ display: 'list-item', padding: 0 }}
                >
                  {v.point.properties?.objectName} {v.field} - {v.message}
                </ListItem>
              ))}
            </List>
            {pointValidations.length > 5 && <>Additional point validation errors unlisted</>}
          </>
        )}
      </>
    );
  }

  const columns = useMemo(
    () => [
      new ColumnDefinition({
        id: 'equip_badges',
        label: 'Status',
        type: 'badges',
        sortable: true,
        sortFunction: (equipA, equipB) => {
          let valueA = equipA.validation?.length || 0;
          let valueB = equipB.validation?.length || 0;
          if (valueB - valueA !== 0) return valueB - valueA;
          valueA = equipA.points?.flatMap((point) => point.validation ?? []).filter(Boolean).length;
          valueB = equipB.points?.flatMap((point) => point.validation ?? []).filter(Boolean).length;
          return valueB - valueA;
        },
        render: (equip) => {
          const statuses = [];

          const promotionState = equipmentPromotionStatus(promotedEquipmentById, equip);
          if (promotionState.status === 'unpublished') {
            statuses.push(
              new StatusBadge({
                id: 'unpublished',
                icon: Unpublished,
                message: 'This equipment is not published.',
              })
            );
          } else if (promotionState.status === 'published') {
            statuses.push(
              new StatusBadge({
                id: 'published',
                icon: PublishedWithChanges,
                color: 'success',
                message: `This equipment was last published ${dateTime(equip.last_promoted)}.`,
              })
            );
          } else {
            statuses.push(
              new StatusBadge({
                id: 'partial',
                icon: ReportProblem,
                color: 'warning',
                message: (
                  <>
                    This equipment was last published {dateTime(equip.last_promoted)}.<br />
                    This equipment has relationships that are unpublished to the following points:
                    <List sx={{ listStyleType: 'disc' }}>
                      {promotionState.unpromotedPoints.map((p) => (
                        <ListItem key={p.topic} sx={{ display: 'list-item', padding: 0 }}>
                          {p.properties?.objectName}
                        </ListItem>
                      ))}
                    </List>
                  </>
                ),
              })
            );
          }

          if (
            equip?.validation?.length > 0 ||
            equip.points?.flatMap((point) => point.validation ?? []).filter(Boolean).length > 0
          ) {
            statuses.push(
              new StatusBadge({
                id: 'publish_error',
                icon: Warning,
                color: 'error',
                message: buildValidationTooltip(equip),
              })
            );
          }
          if (hasUnpublishedChanges(equip))
            statuses.push(
              new StatusBadge({
                id: 'changes',
                icon: ChangeCircle,
                color: 'warning',
                message: `This equipment has unpublished changes.`,
              })
            );
          return statuses;
        },
      }),
      new ColumnDefinition({
        id: 'equip_type',
        label: 'Equipment Type',
        type: 'button',
        action: 'change_equip_type',
        sortable: true,
        render: (equip) => equipTypes[equip.equipment_type_id]?.tagName,
      }),
      new ColumnDefinition({
        id: 'confidence',
        label: 'Equip Type Confidence',
        type: 'linear',
        sortable: true,
        sortFunction: (equipA, equipB) => {
          const valueA = equipA.confidences.equipment_type_id || 0;
          const valueB = equipB.confidences.equipment_type_id || 0;
          if (valueA < valueB) return -1;
          if (valueA > valueB) return 1;
          return 0;
        },
        render: (equip) => equip.confidences.equipment_type_id,
      }),
      new ColumnDefinition({
        id: 'equip_name',
        label: 'Equip Name',
        type: 'text',
        hideable: true,
        render: (equip) => equip.name,
      }),
      new ColumnDefinition({
        id: 'device_id',
        label: 'Device ID',
        type: 'text',
        sortable: true,
        hideable: true,
        render: (equip) => extractDevice(equip, (d) => d.device_id).join(' '),
      }),
      new ColumnDefinition({
        id: 'deployment_site',
        label: 'Deployment Name',
        type: 'text',
        sortable: true,
        hideable: true,
        defaultHidden: true,
        render: (equip) => extractDevice(equip, (d) => deployments[d.deployment_site]).join(' '),
      }),
      new ColumnDefinition({
        id: 'subdeployment_type',
        label: 'Collection Type',
        type: 'text',
        sortable: true,
        hideable: true,
        defaultHidden: true,
        render: (equip) => extractDevice(equip, (d) => d.subdeployment_type).join(' '),
      }),
      new ColumnDefinition({
        id: 'object_name',
        label: 'Device Name',
        type: 'text',
        hideable: true,
        render: (equip) => extractDevice(equip, (d) => d.name).join(' '),
      }),
      new ColumnDefinition({
        id: 'model_name',
        label: 'Device Model',
        type: 'text',
        hideable: true,
        render: (equip) => extractDevice(equip, (d) => d.properties?.modelName).join(' '),
      }),
      new ColumnDefinition({
        id: 'app_soft_ver',
        label: 'Device Software',
        type: 'text',
        hideable: true,
        defaultHidden: true,
        render: (equip) =>
          extractDevice(equip, (d) => d.properties?.applicationSoftwareVersion).join(' '),
      }),
      new ColumnDefinition({
        id: 'device_prog_loc',
        label: 'Device Programs',
        type: 'text',
        hideable: true,
        defaultHidden: true,
        render: (equip) =>
          extractDevice(equip, (d) => JSON.stringify(d.properties?.deviceProgramLocation)).join(
            ' '
          ),
      }),
      new ColumnDefinition({
        id: 'description',
        label: 'Device Description',
        type: 'text',
        hideable: true,
        defaultHidden: true,
        render: (equip) => extractDevice(equip, (d) => d.properties?.description).join(' '),
      }),
      new ColumnDefinition({
        id: 'protocol',
        label: 'Device Protocol',
        type: 'text',
        hideable: true,
        defaultHidden: true,
        render: (equip) => extractDevice(equip, (d) => d.protocol).join(' '),
      }),
      new ColumnDefinition({
        id: 'vendor_name',
        label: 'Device Vendor',
        type: 'text',
        hideable: true,
        defaultHidden: true,
        render: (equip) => extractDevice(equip, (d) => d.properties?.vendorName).join(' '),
      }),
    ],
    [equipTypes, promotedEquipmentById, deployments]
  );

  const changeEquipType = (data, newEquipValues) => {
    const updateRowValues = data.map((datum) => ({
      ...newEquipValues,
      name: datum.name,
    }));
    setLoading(true);
    return updateStagingEquipmentUncaught(updateRowValues).then(() => {
      setLoading(false);
    });
  };

  const demoteEquipment = (removedEquipment) => {
    const relatedPromotedPointIds = Array.from(
      // groupBy/Values to dedupe
      Map.groupBy(
        removedEquipment.flatMap((equip) => equip.points),
        (point) => point.topic
      )
        .values()
        .map((list) => list[0])
        .map((point) => point.promoted_point_id)
        .filter(Boolean)
    );
    const eids = removedEquipment.map((e) => e.promoted_equipment_id).filter(Boolean);
    const orphanedPoints = relatedPromotedPointIds.filter((pid) =>
      promotedPointsById[pid].equipIds.every((eid) => eids.includes(eid))
    );

    return demoteStagingUncaught({
      equipment_ids: removedEquipment.map((e) => e.promoted_equipment_id),
      point_ids: orphanedPoints,
      point_equipment_relationships: removedEquipment.flatMap((equip) =>
        equip.points
          .filter((point) => point.promoted_point_id)
          .map((point) => ({
            equipment_id: equip.promoted_equipment_id,
            point_id: point.promoted_point_id,
          }))
      ),
    });
  };

  const deleteEquipment = (removedEquipment) =>
    deleteStagingEquipmentUncaught(removedEquipment.map((e) => e.equip_id)).then(() =>
      onItemSelected([])
    );

  const onPromotionDialogClosed = (result) => {
    onPromotionResults(result);
    setPromotionOpen(false);
  };

  const actions = [
    new TableAction({
      id: 'change_equip_type',
      label: 'Change Equip Type',
      doSave: changeEquipType,
    }),
    new TableAction({
      id: 'unpublish_equipment',
      label: 'Unpublish',
      doSave: demoteEquipment,
      disabled: (selectedEquipment) =>
        selectedEquipment.map((e) => e.promoted_equipment_id).filter(Boolean).length <= 0,
    }),
    new TableAction({
      id: 'delete_equipment',
      label: 'Delete Empty Equipment',
      doSave: deleteEquipment,
      disabled: (selectedEquipment) =>
        selectedEquipment.map((e) => e.points?.length === 0).filter(Boolean).length === 0 ||
        selectedEquipment.map((e) => e.promoted_equipment_id).filter(Boolean).length > 0,
    }),
  ];

  return (
    <>
      <PromotionDialog
        open={promotionOpen}
        selected={selectedEquipIds}
        equipment={equipment}
        promoteEquipment={promoteEquipment}
        onClose={onPromotionDialogClosed}
      />
      <StagingDataTable
        title="Equipment"
        rowIdKey="equip_id"
        actions={actions}
        columns={columns}
        allData={equipment}
        filteredData={filteredEquipment}
        selectedIds={selectedEquipIds}
        onItemSelected={onItemSelected}
        loading={loading}
        buildingId={buildingId}
      >
        <>
          <Fill />
          <Button
            startIcon=<Publish />
            disabled={selectedEquipIds.length === 0}
            onClick={() => {
              setPromotionOpen(true);
            }}
            sx={{ display: 'flex', marginLeft: 'auto' }}
          >
            Publish {selectedEquipIds.length}
          </Button>
        </>
      </StagingDataTable>
      {getEnv() === 'GCP' ? (
        <Button
          onClick={() => {
            fetchBuildingDboUncaught();
          }}
          sx={{ display: 'flex', marginLeft: 'auto' }}
        >
          Building Config
        </Button>
      ) : null}
    </>
  );
};

const mapStateToProps = (state) => ({
  equipTypes: state.entities.equipTypes.byId,
  promotedPointsById: state.entities.points.byId ?? {},
  promotedEquipmentById: state.entities.equipment.byId ?? {},
  deployments:
    Object.fromEntries(state.entities.deployments.deployments?.map((d) => [d.site, d.name])) ?? {},
});

const mapDispatchToProps = (dispatch, { buildingId }) => ({
  promoteEquipment: (equipmentList, topics) =>
    dispatch(promoteEquipsTopicsAction(buildingId, equipmentList, topics)),
  updateStagingEquipmentUncaught: (equipId, data) =>
    dispatch(updateStagingEquipment(buildingId, equipId, data)),
  getPointDetails: (topics) => dispatch(getPointDetailsAction(buildingId, topics)),
  getEquipmentDetails: (topics) => dispatch(getEquipmentDetailsAction(buildingId, topics)),
  demoteStagingUncaught: (data) => dispatch(demoteStaging(buildingId, data)),
  deleteStagingEquipmentUncaught: (equipIds) =>
    dispatch(stagingEquipmentDelete(buildingId, equipIds)),
  fetchBuildingDboUncaught: () => dispatch(fetchBuildingDbo(buildingId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EquipmentDataTable);
